import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  JobStats,
  UpcomingSchedule,
} from "@property-reports/api-contract";

export function useAdminJobs() {
  return useQuery<JobStats>({
    queryKey: ["admin", "jobs"],
    queryFn: async () => {
      const response = await fetch("/api/admin/jobs");
      if (!response.ok) throw new Error("Failed to fetch job stats");
      return response.json();
    },
    refetchInterval: 10000,
  });
}

export function useUpcomingSchedule() {
  return useQuery<UpcomingSchedule>({
    queryKey: ["admin", "jobs", "schedule"],
    queryFn: async () => {
      const response = await fetch("/api/admin/jobs/schedule");
      if (!response.ok) throw new Error("Failed to fetch schedule");
      return response.json();
    },
    refetchInterval: 60000,
  });
}

export function usePauseJobs() {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["admin", "jobs", "pause"],
    queryFn: async () => {
      const response = await fetch("/api/admin/jobs/pause");
      if (!response.ok) throw new Error("Failed to fetch pause state");
      return response.json();
    },
  });

  const mutation = useMutation({
    mutationFn: async (paused: boolean) => {
      const response = await fetch("/api/admin/jobs/pause", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paused }),
      });
      if (!response.ok) throw new Error("Failed to toggle pause");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "jobs", "pause"] });
    },
  });

  return {
    paused: data?.paused ?? false,
    toggle: () => mutation.mutate(!data?.paused),
    isLoading: mutation.isPending,
  };
}
